import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { finalize, timeout } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AdminService } from '../../../shared/services/admin.service';

@Component({
    selector: 'app-weather',
    templateUrl: './weather.component.html',
    styleUrls: ['./weather.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WeatherComponent implements OnInit, OnDestroy {
  scout: any;
  loading = false;
  loadingScouts = false;
  availableScouts: any[] = [];
  selectedScoutName = '';
  errorMessage = '';
  private scoutsSubscription: Subscription;
  private readingsSubscription: Subscription;
  hasReadings = false;
  airTemp: any;
  barometricPressure: any;
  precipitation: any;
  shortwaveRadiation: any;
  windDirection: any;
  windSpeedCombinedNE: any;
  

  constructor(private adminService: AdminService) { }

  ngOnInit(): void {
    this.loadScouts();
  }

  assignValues() {
    const values = this.scout?.sensors?.find(sensor => (sensor.code || '').includes('METERATM'))?.readingTypes;
    const reading = (index: number, separator = ' ') => {
      const item = values?.[index];
      const value = item?.statistics?.value;
      return value == null ? null : `${value}${separator}${item.unit || ''}`.trim();
    };
    this.airTemp = reading(1, ' °');
    this.barometricPressure = reading(2);
    this.precipitation = reading(6);
    this.shortwaveRadiation = reading(8);
    const direction = values?.[10]?.statistics?.value;
    this.windDirection = direction == null ? null : `${direction}°`;
    this.windSpeedCombinedNE = reading(12);
    this.hasReadings = [this.airTemp, this.barometricPressure, this.precipitation,
      this.shortwaveRadiation, this.windDirection, this.windSpeedCombinedNE].some(value => value != null);
  }

  loadScouts() {
    this.scoutsSubscription?.unsubscribe();
    this.loadingScouts = true;
    this.errorMessage = '';
    this.scoutsSubscription = this.adminService.getAppScouts()
      .pipe(timeout(20000), finalize(() => this.loadingScouts = false))
      .subscribe({
        next: scouts => {
          if (!Array.isArray(scouts)) {
            this.errorMessage = 'Scouts could not be loaded. Refresh to try again.';
            return;
          }
          this.availableScouts = scouts.filter(item => item.name)
            .sort((a, b) => a.name.localeCompare(b.name));
          const selected = this.availableScouts.find(item => item.name === this.selectedScoutName)
            || this.availableScouts.find(item => item.name === 'EARTHS-000295' || item.code === 'EARTHS-000295');
          if (selected) {
            this.selectedScoutName = selected.name;
            this.getScout(selected.name);
          }
        },
        error: () => this.errorMessage = 'Scouts could not be loaded. Refresh to try again.'
      });
  }

  refresh() {
    if (this.selectedScoutName) {
      this.getScout(this.selectedScoutName);
    } else {
      this.loadScouts();
    }
  }

  getScout(scoutName: string) {
    if (!scoutName) { return; }
    this.readingsSubscription?.unsubscribe();
    this.selectedScoutName = scoutName;
    this.scout = null;
    this.hasReadings = false;
    this.errorMessage = '';
    this.loading = true;
    this.readingsSubscription = this.adminService.getAppScoutTreeFwmByName(scoutName)
      .pipe(timeout(20000), finalize(() => this.loading = false))
      .subscribe({
        next: scouts => {
          if (!Array.isArray(scouts)) {
            this.errorMessage = 'Weather readings could not be loaded. Refresh to try again.';
            return;
          }
          this.scout = scouts.find(item => item.name === scoutName) || (scouts.length === 1 ? scouts[0] : null);
          if (this.scout) {
            this.assignValues();
          } else {
            this.errorMessage = 'The selected Scout was not found. Choose another Scout or refresh.';
          }
        },
        error: () => this.errorMessage = 'Weather readings could not be loaded. Refresh to try again.'
      });
  }

  ngOnDestroy() {
    this.scoutsSubscription?.unsubscribe();
    this.readingsSubscription?.unsubscribe();
  }
}
